import React from 'react';
import { motion } from 'framer-motion';
import { contactInfo, personalInfo } from '../data/portfolio';
import { openCalendar } from '../utils/calendar';
import './Footer.css';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    {
      name: 'LinkedIn',
      icon: 'fab fa-linkedin',
      url: contactInfo.linkedin,
      color: '#0077b5'
    },
    {
      name: 'GitHub',
      icon: 'fab fa-github',
      url: contactInfo.github,
      color: '#333'
    },
    {
      name: 'Email',
      icon: 'fas fa-envelope',
      url: `mailto:${contactInfo.email}`,
      color: '#ea4335'
    }
  ];


  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-content">
          <motion.div
            className="footer-section"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="footer-brand">
              <div className="footer-brand-title">
                <svg
                  className="logo-mark"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 160 160"
                  fill="none"
                  aria-label="Logo Cyrille Tual"
                  role="img"
                >
                  <g transform="translate(80 80)">
                    <ellipse rx="68" ry="26" stroke="#fbf9f5" strokeWidth="5" fill="none" transform="rotate(0)" />
                    <ellipse rx="68" ry="26" stroke="#fbf9f5" strokeWidth="5" fill="none" transform="rotate(60)" />
                    <ellipse rx="68" ry="26" stroke="#fbf9f5" strokeWidth="5" fill="none" transform="rotate(120)" />
                    <circle r="26" fill="#1E63F5" />
                    <text y="9" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontWeight="800" fontSize="26" letterSpacing="-1" fill="#fbf9f5">CT</text>
                  </g>
                </svg>
                <h3>
                  <span className="brand-highlight">Cyrille</span> Tual
                </h3>
              </div>

              <p>Développeur Front-End React / TypeScript</p>
            </div>

            <div className="social-links">
              {socialLinks.map((link, index) => (
                <motion.a
                  key={link.name}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link"
                  style={{ '--hover-color': link.color } as React.CSSProperties}
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <i className={link.icon}></i>
                  <span className="sr-only">{link.name}</span>
                </motion.a>
              ))}
            </div>
          </motion.div>

          <motion.div
            className="footer-section"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <h4>Navigation</h4>
            <nav className="footer-nav">
              <button onClick={() => scrollToSection('hero')} className="footer-link">
                Accueil
              </button>
              <button onClick={() => scrollToSection('mission')} className="footer-link">
                Actualité
              </button>
              <button onClick={() => scrollToSection('skills')} className="footer-link">
                Compétences
              </button>
              <button onClick={() => scrollToSection('projects')} className="footer-link">
                Portfolio
              </button>
              <button onClick={() => scrollToSection('contact')} className="footer-link">
                Contact
              </button>
            </nav>
          </motion.div>

          <motion.div
            className="footer-section"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h4>Contact</h4>
            <div className="contact-info">
              <a href={`mailto:${contactInfo.email}`} className="contact-item">
                <i className="fas fa-envelope"></i>
                {contactInfo.email}
              </a>
              <a href={personalInfo.cvUrl} target="_blank" rel="noopener noreferrer" className="contact-item">
                <i className="fas fa-file-lines"></i>
                Voir le CV
              </a>
              <button onClick={openCalendar} className="contact-item footer-rdv-btn">
                <i className="fas fa-calendar-alt"></i>
                Prendre rendez-vous
              </button>
            </div>
          </motion.div>

          <motion.div
            className="footer-section"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <h4>Technologies</h4>
            <div className="tech-stack">
              <span className="tech-item">React 19</span>
              <span className="tech-item">TypeScript</span>
              <span className="tech-item">MUI</span>
              <span className="tech-item">TanStack Query</span>
              <span className="tech-item">Zustand</span>
              <span className="tech-item">Lexical</span>
            </div>
          </motion.div>
        </div>

        <motion.div
          className="footer-bottom"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <div className="footer-bottom-content">
            <p className="copyright">
              © {currentYear} {personalInfo.name}. Tous droits réservés.
            </p>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;